import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { LinearProgress } from '@material-ui/core';

import { AnalyticsTracker, useAnalytics } from '@backstage/core-plugin-api';

import { useAuth } from '@spotify/confidence-core-react';
import { useCollaborativeDocument } from '@spotify/confidence-editor-react';
import { useAccounts } from '@spotify/plugin-confidence-app-react';

import { navigateToUrl, urlFromResource } from './utils';

const notificationClicked = (
  params: URLSearchParams,
  analytics: AnalyticsTracker,
) => {
  const activityId = params.get('activity');
  if (activityId) {
    analytics.captureEvent('notification-clicked', 'redirect', {
      attributes: {
        activityId: activityId,
      },
    });
  }
};

const COLLABORATIVE_DOCUMENTS_PREFIX = 'collaborativeDocuments/';

const RedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedAccountId, isLoading, isSwitching, switchAccount } =
    useAccounts();

  const {
    isAuthenticated,
    isLoading: authIsLoading,
    loginWithRedirect,
  } = useAuth();
  const analytics = useAnalytics();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const resource = params.get('resource');
  const account = params.get('account');
  const isCollaborativeDocument =
    resource?.startsWith(COLLABORATIVE_DOCUMENTS_PREFIX) ?? false;

  // Defer the query until auth is settled and we're on the correct account,
  // so that auth/account-switch flows aren't blocked by a failing query.
  const authReady = !authIsLoading && isAuthenticated;
  const accountReady =
    !isLoading && (account === null || account === selectedAccountId);

  const {
    collaborativeDocument,
    isLoading: collabDocLoading,
    error: collabDocError,
  } = useCollaborativeDocument({
    name: resource ?? '',
    skip: !isCollaborativeDocument || !authReady || !accountReady,
  });

  useEffect(() => {
    if (!resource) return;

    // For collaborative documents, use the current redirect URL as returnTo
    // so the handler re-runs after auth/account-switch settles.
    const currentRedirectUrl = `${location.pathname}${location.search}`;

    // Handle auth redirect
    if (!authIsLoading && !isAuthenticated) {
      const returnTo = isCollaborativeDocument
        ? currentRedirectUrl
        : urlFromResource(resource);
      notificationClicked(params, analytics);
      loginWithRedirect({ appState: { returnTo } });
      return;
    }

    // Handle account switching
    if (
      account &&
      account !== selectedAccountId &&
      !isLoading &&
      !isSwitching
    ) {
      const returnTo = isCollaborativeDocument
        ? currentRedirectUrl
        : urlFromResource(resource);
      notificationClicked(params, analytics);
      switchAccount(account, returnTo);
      return;
    }

    // For non-collaborative-document resources, redirect immediately
    if (!isCollaborativeDocument) {
      if (account === null || account === selectedAccountId) {
        const newUrl = urlFromResource(resource);
        notificationClicked(params, analytics);
        navigateToUrl(newUrl);
      }
      return;
    }

    // Wait for auth and account to settle before evaluating the query result.
    // When skipped, the hook returns isLoading: false with no data, so without
    // this guard we'd fall through to the error branch prematurely.
    if (!authReady || !accountReady) return;

    // For collaborative documents, wait for the query to complete
    if (collabDocLoading) return;

    if (collabDocError || !collaborativeDocument?.resource) {
      navigate('/', { replace: true });
      return;
    }

    notificationClicked(params, analytics);
    navigateToUrl(`/${collaborativeDocument.resource}/report`);
  }, [
    selectedAccountId,
    isLoading,
    isSwitching,
    switchAccount,
    isAuthenticated,
    authIsLoading,
    loginWithRedirect,
    isCollaborativeDocument,
    collaborativeDocument,
    collabDocLoading,
    collabDocError,
    authReady,
    accountReady,
    params,
    resource,
    account,
    analytics,
    location,
    navigate,
  ]);

  return <LinearProgress />;
};

export default RedirectHandler;
